import { Row, RowData } from '@tanstack/react-table';

/**
 * Find the closest selected in-view row to the newly selected row
 * @param row - newly selected row
 * @param rows - currently selected rows
 */
const findClosestPreviousRow = <TData extends RowData>(
  row: Row<TData>,
  rows: Row<TData>[],
) => {
  let minIndex = 0;
  const rowsInView = rows.filter((r) => r.index >= 0);

  // initialize minIndex to the first row in view
  if (rowsInView?.length) {
    minIndex = rows.indexOf(rowsInView[0]);
  }

  let i = 0;
  while (i < rows.length && rows[i].index < row.index) {
    if (rows[i].index >= 0) {
      minIndex = i;
    }
    i++;
  }

  return rows[minIndex];
};

/**
 * Remap index property in each row to account for sorting using allRows order
 * @param row - newly selected row
 * @param selectedRows - currently selected rows
 * @param allRows - all rows in the table
 */
const remapTableIndices = <TData extends RowData>(
  row: Row<TData>,
  selectedRows: Row<TData>[],
  allRows: Row<TData>[],
) => {
  const newRow = { ...row };
  const newSelectedRows = [...selectedRows];
  const newAllRows = [...allRows];

  newRow.index = allRows.findIndex((r) => r.index === row.index);

  newSelectedRows.forEach((r) => {
    r.index = allRows.findIndex((row) => row.id === r.id);
  });

  for (let i = 0; i < allRows.length; i++) {
    newAllRows[i].index = i;
  }

  return {
    mappedRow: newRow,
    mappedSelectedRows: newSelectedRows,
    mappedAllRows: newAllRows,
  };
};

export const getMultiRowRangeSelection = <TData extends RowData>(
  row: Row<TData>,
  selectedRows: Row<TData>[],
  allRows: Row<TData>[],
) => {
  // remap row indices to account for sorting
  const { mappedRow, mappedSelectedRows, mappedAllRows } = remapTableIndices(
    row,
    selectedRows,
    allRows,
  );

  const prevRow = findClosestPreviousRow(mappedRow, mappedSelectedRows);
  if (!prevRow) return {};
  // if the closest row is not visible then just select the current row
  if (prevRow.index < 0) return { [row.id]: true };

  let firstRow = prevRow;
  let lastRow = mappedRow;
  let inc = 1;

  // user is selecting in reverse order so swap variables
  if (row.index < prevRow.index) {
    firstRow = mappedRow;
    lastRow = prevRow;
    inc = 0;
  }

  const firstAndLastParentRowIds = [
    ...firstRow.getParentRows().map((r) => r.id),
    ...lastRow.getParentRows().map((r) => r.id),
  ];

  const getNestedSubRowSelections = (
    rows: Row<TData>[],
  ): Record<string, boolean> => {
    return rows.reduce((acc, row) => {
      return {
        ...acc,
        [row.id]: true,
        ...(row.subRows ? getNestedSubRowSelections(row.subRows) : {}),
      };
    }, {});
  };

  const newSelections = Array.from({
    length: Math.abs(lastRow.index - firstRow.index),
  }).reduce((accum: Record<string, boolean>, _, index) => {
    const nextRow = mappedAllRows.find(
      (r) => r.index === index + inc + firstRow.index,
    );
    if (nextRow) {
      if (
        // If nextRow is a parent of the first or last row: Do not initially select its id
        // Once we've figured out all the selected rows: We'll determine if we should select the parent rows
        // based on whether all their subRows are selected or not
        firstAndLastParentRowIds.includes(nextRow.id)
      ) {
        return { ...accum };
      }

      let nextRowSelectedIds = { [nextRow.id]: true };
      // If nextRow has subRows and either:
      // 1: nextRow is the lastRow. Or
      // 2: nextRow is not expanded
      // Then we need to manually add in all of its subRows to be selected
      if (
        nextRow.subRows?.length > 0 &&
        (nextRow.id === lastRow.id || !nextRow.getIsExpanded())
      ) {
        nextRowSelectedIds = {
          ...nextRowSelectedIds,
          ...getNestedSubRowSelections([nextRow]),
        };
      }

      return {
        ...accum,
        ...nextRowSelectedIds,
      };
    }
    return accum;
  }, {});

  const selectedRowIds = mappedSelectedRows.map((row) => row.id);

  // Add any of the lastRow's parents as selected if all of their children are selected
  if (lastRow.getParentRows().length > 0) {
    lastRow
      .getParentRows()
      .reverse()
      .forEach((parentRow) => {
        if (parentRow.subRows?.length > 0) {
          const subRowIds = parentRow.subRows.map((subRow) => subRow.id);
          if (
            !newSelections[parentRow.id] &&
            !selectedRowIds.includes(parentRow.id) &&
            subRowIds.every(
              (subRowId) =>
                newSelections[subRowId] || selectedRowIds.includes(subRowId),
            )
          ) {
            newSelections[parentRow.id] = true;
          }
        }

        return parentRow;
      });
  }

  // Add any of the firstRow's parents as selected if all their children are selected
  if (firstRow.getParentRows().length > 0) {
    firstRow
      .getParentRows()
      .reverse()
      .forEach((parentRow) => {
        if (parentRow.subRows?.length > 0) {
          const subRowIds = parentRow.subRows.map((subRow) => subRow.id);
          if (
            !newSelections[parentRow.id] &&
            !selectedRowIds.includes(parentRow.id) &&
            subRowIds.every(
              (subRowId) =>
                newSelections[subRowId] || selectedRowIds.includes(subRowId),
            )
          ) {
            newSelections[parentRow.id] = true;
          }
        }
      });
  }

  return newSelections;
};

export const getDisableHighlightOnMouseDown = <TData extends RowData>({
  enableMultiRowRangeSelection,
  selectedRows,
}: {
  enableMultiRowRangeSelection: boolean;
  selectedRows: Row<TData>[];
}): React.MouseEventHandler<HTMLDivElement> | undefined => {
  if (!enableMultiRowRangeSelection) {
    return undefined;
  }

  return (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e?.shiftKey && selectedRows.length) {
      e.preventDefault();
    }
  };
};
